import React from "react";
import { ExpandableHeader } from "../../../../../components/ACS-Dashboard/ACS-Blocks/ExpandableHeader";
import ActivityLogDrawer, {
  PreviewSkeleton,
  formatValueByField,
  getStatusClasses,
  getStatusLabel,
  normalizeUpdatedBy,
} from "../../../../../components/ACS-Dashboard/ACS-Blocks/ActivityLogDrawer";
import { Activity, Clock3, GitCompare } from "lucide-react";

const ShipmentActivity = ({
  setSectionRef,
  isActivityLoading,
  activityLogs,
  formatDisplayDate,
  isOpen,
  onOpen,
  onClose,
  showInline = true,
}) => {
  const safeLogs = Array.isArray(activityLogs) ? activityLogs : [];
  const previewLogs = safeLogs.slice(0, 3);
  const hasActivity = safeLogs.length > 0;

  const openDrawer = () => onOpen?.();
  const closeDrawer = () => onClose?.();

  return (
    <>
      {showInline ? (
        <div className="mt-fluid-4 font-roboto text-fluid-base">
          <div className="mb-fluid-2 flex items-center justify-between">
            <h3
              ref={setSectionRef("Shipment Activity")}
              className="text-fluid-lg font-semibold"
            >
              Shipment Activity
            </h3>
            <span className="text-xs text-gray-500 dark:text-acs-dark-subtext">
              Slides in from the right for the full story.
            </span>
          </div>

          <ExpandableHeader
            headerLabel="Activity Timeline"
            icon={Activity}
            headerType="primary"
            initiallyExpanded
          >
            <div className="space-y-fluid-3 rounded-xl border border-gray-200 bg-white p-fluid-3 shadow-sm dark:border-acs-dark-border dark:bg-acs-dark-surface">
              <div className="flex flex-wrap items-start justify-between gap-fluid-2">
                <div>
                  <p className="text-fluid-base font-semibold text-gray-900 dark:text-acs-dark-text">
                    Timeline preview
                  </p>
                  <p className="text-sm text-gray-600 dark:text-acs-dark-subtext">
                    Open the slide-in panel to explore every change on this
                    shipment.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-acs-secondary-mint px-3 py-1 text-xs font-semibold text-acs-primary-navy dark:bg-acs-primary-blue/25 dark:text-acs-dark-text">
                    {safeLogs.length} updates
                  </span>
                  <button
                    type="button"
                    onClick={openDrawer}
                    className="inline-flex items-center gap-2 rounded-md bg-acs-primary-navy px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-acs-primary-blue focus:outline-none focus:ring-2 focus:ring-acs-accent focus:ring-offset-2"
                  >
                    <GitCompare size={16} />
                    Open timeline
                  </button>
                </div>
              </div>

              {isActivityLoading ? (
                <PreviewSkeleton />
              ) : hasActivity ? (
                <div className="space-y-fluid-2">
                  {previewLogs.map((item, idx) => {
                    const formattedDate = formatDisplayDate
                      ? formatDisplayDate(item.updatedTime)
                      : item.updatedTime || "—";
                    return (
                      <div
                        key={`${item.updatedTime}-${idx}`}
                        className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-fluid-2 shadow-sm dark:border-acs-dark-border dark:bg-acs-dark-bg"
                      >
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-acs-secondary-mint text-acs-primary-navy font-semibold">
                          {idx + 1}
                        </div>
                        <div className="flex-1 space-y-1">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${getStatusClasses(
                                  item.status
                                )}`}
                              >
                                <Activity size={14} />
                                {getStatusLabel(item.status)}
                              </span>
                              {item.fieldName ? (
                                <span className="rounded-full bg-white/80 px-2 py-1 text-xs text-gray-700 dark:bg-acs-dark-surface dark:text-acs-dark-text">
                                  {item.fieldName}
                                </span>
                              ) : null}
                            </div>
                            <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-1 text-xs text-gray-600 shadow-sm dark:bg-acs-dark-surface dark:text-acs-dark-subtext">
                              <Clock3 size={14} />
                              {formattedDate}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-acs-dark-subtext">
                            {normalizeUpdatedBy(item.updatedBy) || "System"} • Shipment #
                            {item.shipmentNumber || "—"}
                          </p>
                          {item.reason ? (
                            <p className="text-xs text-gray-700 dark:text-acs-dark-text">
                              {item.reason}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-fluid-3 text-sm text-gray-600 dark:border-acs-dark-border dark:bg-acs-dark-bg dark:text-acs-dark-subtext">
                  No activity logs yet. Use the Activity button near Print to
                  slide in the full timeline when updates arrive.
                </div>
              )}
            </div>
          </ExpandableHeader> 
        </div>
      ) : null}

      <ActivityLogDrawer
        isOpen={isOpen}
        onClose={closeDrawer}
        logs={safeLogs}
        formatDisplayDate={formatDisplayDate}
        isActivityLoading={isActivityLoading}
        eyebrow="Shipment Timeline"
        title="Activity Log"
        entityLabel="Shipment"
        entityNumberKey="shipmentNumber"
        valueFormatter={formatValueByField}
      />
    </>
  );
};

export default ShipmentActivity;
